import { Form, Formik, type FormikHelpers } from "formik";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  IRL_REGIONS,
  PROPERTY_INITIAL_VALUES,
  propertySchema,
} from "../../models/property.js";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../hooks/use-property-folders";
import Finance from "./AddFormComponents/Finance";
import FinanceEssentials from "./AddFormComponents/FinanceEssentials";
import EquipementsLocal from "./AddFormComponents/EquipementsLocal";
import HeaderPropertyForm from "./AddFormComponents/HeaderPropertyForm";
import {
  sanitizeValues,
  toInitialValuesFromSchema,
  // @ts-expect-error pure js
} from "../../models/utils.js";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
import useEdlPiecesPrompt from "../../hooks/use-edl-pieces-prompt";
// @ts-expect-error pure js
import TabbedForm from "../UI/TabbedForm";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import TypeBienForm from "./AddFormComponents/TypeBienForm";
import TypeLogement from "./AddFormComponents/TypeLogement";
import TypeLocation from "./AddFormComponents/TypeLocation";
import NbAppartements from "./AddFormComponents/NbAppartements";
import styles from "./PropertyForm.module.css";

interface PropertyFormProps {
  edit?: boolean;
}

interface PropertyFormValues {
  type?: string;
  is_immeuble?: boolean;
  nb_rooms?: string | number;
}

interface TabbedFormHandle {
  resetTab: () => void;
}

export default function PropertyForm({ edit }: PropertyFormProps) {
  const { propertyId } = useParams();
  const nav = useNavigate();
  const [error, setError] = useState("");
  const { properties, addProperty, updateProperty } = useProperties();
  const { createPropertyFolder, mutate: refreshFolders } = usePropertyFolders();

  const [Modal, closeModal] = useModal(true);
  const formRef = useRef<TabbedFormHandle>(null);
  const { confirmDialog, promptIfPiecesAdded } = useEdlPiecesPrompt(
    parseInt(propertyId as string),
  );

  let initialValues = PROPERTY_INITIAL_VALUES;

  if (edit) {
    const property = properties.find(
      (b: { id: number }) => b.id === parseInt(propertyId as string),
    );
    initialValues = toInitialValuesFromSchema(
      propertySchema,
      property,
      PROPERTY_INITIAL_VALUES,
    );
    // Le back stocke la région IRL sur le bien ; le radio du formulaire ne
    // connaît que la valeur "IRL", résolue en région à la sauvegarde
    if (
      initialValues.type === "garage" &&
      (!initialValues.type_indice ||
        initialValues.type_indice === "Aucun" ||
        IRL_REGIONS.includes(initialValues.type_indice))
    ) {
      initialValues = { ...initialValues, type_indice: "IRL" };
    }
  }

  async function submitHandler(
    values: PropertyFormValues,
    { setSubmitting }: FormikHelpers<PropertyFormValues>,
  ) {
    const property = { ...sanitizeValues(values) };
    // Plus saisi à la création : le back affecte un template par défaut, et la
    // colonne n'accepte pas null
    if (property.id_lease_template == null) {
      delete property.id_lease_template;
    }
    try {
      if (edit) {
        const propertyBefore = properties.find(
          (b: { id: number }) => b.id === parseInt(propertyId as string),
        );
        await updateProperty(parseInt(propertyId as string), property);
        promptIfPiecesAdded(propertyBefore, property);
        closeModal();
      } else if (property.is_immeuble && parseInt(property.nb_rooms) === 0) {
        await createPropertyFolder(property);
        closeModal();
      } else {
        const newProperty = await addProperty(property);
        await refreshFolders();
        if (!property.is_immeuble && newProperty?.id) {
          // Arrivée directe sur la fiche du bien créé (le guide de première
          // visite s'y déclenche). Surtout pas de closeModal() ici : la
          // fermeture de la modale routée fait un navigate('..') vers la
          // liste, qui écraserait cette redirection.
          nav(`/bailleur/properties/${newProperty.id}`);
        } else {
          closeModal();
        }
      }
      setError("");
    } catch (error) {
      setError(handleAPIError(error));
    }
    setSubmitting(false);
  }

  return (
    <>
      {confirmDialog}
      <Formik<PropertyFormValues>
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={submitHandler}
        validationSchema={propertySchema}
      >
        {(formikProps) => {
          let tabIndex = 0;
          return (
            <>
              <TabbedForm id="property-form" ref={formRef}>
                <Modal
                  confirmCloseText={
                    "Votre formulaire n'a pas été sauvegardé. Voulez-vous vraiment quitter ?"
                  }
                  shouldConfirm={() => formikProps.dirty}
                  size={"big"}
                  stickyHeader
                  blueHeader
                  onClose={() => {
                    formRef.current?.resetTab();
                  }}
                  header={
                    <TabbedForm.TabList>
                      <TabbedForm.Tab panelId="type-bien" index={tabIndex++}>
                        Type de bien
                      </TabbedForm.Tab>
                      {formikProps.values.type && (
                        <>
                          {formikProps.values.type === "habitation" &&
                            !formikProps.values.is_immeuble && (
                              <>
                                <TabbedForm.Tab
                                  panelId="type-logement"
                                  index={tabIndex++}
                                >
                                  Type de logement
                                </TabbedForm.Tab>
                                <TabbedForm.Tab
                                  panelId="type-locataire"
                                  index={tabIndex++}
                                >
                                  Type de locataire
                                </TabbedForm.Tab>
                              </>
                            )}
                          {formikProps.values.is_immeuble && (
                            <TabbedForm.Tab
                              panelId="nb-apparts"
                              index={tabIndex++}
                            >
                              Appartements
                            </TabbedForm.Tab>
                          )}
                          <TabbedForm.Tab panelId="bien" index={tabIndex++}>
                            Le bien
                          </TabbedForm.Tab>
                          {(!formikProps.values.is_immeuble ||
                            parseInt(String(formikProps.values.nb_rooms)) >
                              0) && (
                            <>
                              {(edit || formikProps.values.is_immeuble) && (
                                <TabbedForm.Tab
                                  panelId="finance-fiscalite"
                                  index={tabIndex++}
                                >
                                  Finance et fiscalité
                                </TabbedForm.Tab>
                              )}
                              <TabbedForm.Tab
                                panelId="equipements"
                                index={tabIndex++}
                              >
                                Equipements
                              </TabbedForm.Tab>
                            </>
                          )}
                        </>
                      )}
                    </TabbedForm.TabList>
                  }
                >
                  <Form>
                    <div className={styles.content}>
                      <TabbedForm.TabPanel id="type-bien">
                        <TypeBienForm />
                      </TabbedForm.TabPanel>
                      <TabbedForm.TabPanel id="type-logement">
                        <TypeLogement
                          setFieldValue={formikProps.setFieldValue}
                        />
                      </TabbedForm.TabPanel>
                      <TabbedForm.TabPanel id="type-locataire">
                        <TypeLocation />
                      </TabbedForm.TabPanel>
                      <TabbedForm.TabPanel id="nb-apparts">
                        <NbAppartements />
                      </TabbedForm.TabPanel>
                      <TabbedForm.TabPanel id="bien">
                        <HeaderPropertyForm />
                        {!edit && !formikProps.values.is_immeuble && (
                          <FinanceEssentials />
                        )}
                        <TabbedForm.Actions error={error} />
                      </TabbedForm.TabPanel>
                      {(edit || formikProps.values.is_immeuble) && (
                        <TabbedForm.TabPanel id="finance-fiscalite">
                          <Finance edit={edit} />
                          <TabbedForm.Actions error={error} />
                        </TabbedForm.TabPanel>
                      )}
                      <TabbedForm.TabPanel id="equipements">
                        <EquipementsLocal isCreation={!edit} />
                        <TabbedForm.Actions error={error} />
                      </TabbedForm.TabPanel>
                    </div>
                  </Form>
                </Modal>
              </TabbedForm>
            </>
          );
        }}
      </Formik>
    </>
  );
}
